import { lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Facebook, Instagram } from 'lucide-react';

// Lazy: zieht react-hook-form/zod (forms-Chunk, ~21 KB gzip) aus dem
// kritischen Entry-Pfad — der Strip liegt ohnehin unter dem Fold (12.TP1).
const NewsletterSignup = lazy(() =>
  import('@/components/newsletter/NewsletterSignup').then((m) => ({
    default: m.NewsletterSignup,
  })),
);
import {
  useRestaurantSettings,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type OpeningHour,
} from '@/features/restaurant/useRestaurantSettings';
import { ChatbotConsentFooterLink } from '@/features/chatbot/ChatbotConsentFooterLink';

/**
 * Public-Footer — 1:1-Umsetzung von Die Sizilianer.html `<footer>`.
 *
 * Klassen-Namen kommen aus src/styles/sizilianer-design.css (siehe `footer`-Block).
 * 5-Spalten-Grid mit Inline-SVG-Icons in jeder Heading-Zelle.
 * + Newsletter-Strip oberhalb des Grids (Sprint 5).
 *
 * Inhalt kommt aus restaurant_settings — Admin kann via /admin/einstellungen
 * Adresse / Phone / Öffnungszeiten / Social-Links ändern ohne Code-Deploy.
 * Fallback: hardcoded Werte für initiales Loading.
 */
export function PublicFooter() {
  const { data: settings } = useRestaurantSettings();
  const { t } = useTranslation();
  const location = useLocation();
  // 0070: Kill-Switch — Newsletter komplett von der Public-Site nehmen
  // (Toggle in /admin/newsletter, sofort wirksam ohne Deploy).
  const newsletterEnabled = settings?.newsletter_enabled !== false;
  // F-017: Auf der Newsletter-Seite gibt es bereits ein Anmeldeformular —
  // den Footer-Newsletter-Strip dort nicht zusätzlich rendern (kein Doppel).
  const hideNewsletterStrip = !newsletterEnabled || location.pathname === '/newsletter';

  const address =
    settings
      ? `${settings.address_street}, ${settings.address_zip} ${settings.address_city}`
      : 'Im Werth 25, 46282 Dorsten';
  const phone = settings?.phone ?? '02362 23279';
  const phoneHref = `tel:${(settings?.phone ?? '+49 2362 23279').replace(/\s/g, '')}`;
  const restaurantName = settings?.name ?? 'Die Sizilianer';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const socials = (settings?.socials_jsonb ?? {}) as any;
  const facebookUrl = socials?.facebook ?? 'https://facebook.com/diesizilianer';
  const instagramUrl = socials?.instagram ?? 'https://instagram.com/diesizilianer';

  return (
    <>
      {!hideNewsletterStrip && (
        <section className="bg-paper-2 border-y border-rule px-6 py-10">
          <div className="mx-auto max-w-4xl flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="md:flex-1">
              <h3 className="font-serif text-xl text-ink-2 mb-1 leading-tight">
                {t('footer.newsletter.title')}
              </h3>
              <p className="font-sans text-sm text-ink-soft">
                <Trans
                  i18nKey="footer.newsletter.lede"
                  components={{ strong: <strong /> }}
                />
              </p>
            </div>
            <div className="md:flex-1 md:max-w-md w-full">
              {/* Platzhalter in Formularhöhe verhindert Layout-Shift */}
              <Suspense fallback={<div className="h-11" aria-hidden="true" />}>
                <NewsletterSignup variant="inline" source="footer" />
              </Suspense>
            </div>
          </div>
        </section>
      )}
      <footer>
      <div className="col">
        <h5>
          <span className="ic">
            <svg viewBox="0 0 24 24">
              <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
              <circle cx="12" cy="9" r="2.5" />
            </svg>
          </span>
          {restaurantName}
        </h5>
        <p>{address}</p>
      </div>
      <div className="col">
        <h5>
          <span className="ic">
            <svg viewBox="0 0 24 24">
              <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
            </svg>
          </span>
          {t('footer.phone')}
        </h5>
        <p>
          <a href={phoneHref}>{phone}</a>
        </p>
      </div>
      <div className="col">
        <h5>
          <span className="ic">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </span>
          {t('footer.hours')}
        </h5>
        <OpeningHoursList hours={settings?.opening_hours_jsonb} />
      </div>
      <div className="col">
        <h5>{t('footer.follow')}</h5>
        <div className="social">
          {facebookUrl && (
            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
            >
              <Facebook size={14} strokeWidth={1.5} fill="currentColor" />
            </a>
          )}
          {instagramUrl && (
            <a
              href={instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <Instagram size={14} strokeWidth={1.5} />
            </a>
          )}
        </div>
      </div>
      <div className="col">
        <h5>&nbsp;</h5>
        <Link to="/la-putia">La Putia</Link>
        <Link to="/impressum">{t('nav.impressum')}</Link>
        <Link to="/datenschutz">{t('nav.privacy')}</Link>
        {newsletterEnabled && <Link to="/newsletter">{t('nav.newsletter')}</Link>}
        <ChatbotConsentFooterLink />
        {/*
          Versteckter Admin-Eingang — nur Mitarbeiter wissen davon.
          Discrete unicode-dot mit niedriger Opacity, kein Tooltip, kein aria-label.
          Funktioniert wie ein Easter-Egg: hover → leicht sichtbar, klick → /admin/login.
        */}
        <Link
          to="/admin/login"
          aria-hidden="true"
          tabIndex={-1}
          className="mt-3 inline-block text-2xs opacity-20 hover:opacity-100 transition-opacity duration-300 select-none"
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          ·
        </Link>
      </div>
    </footer>
    </>
  );
}

/**
 * Rendert Öffnungszeiten kompakt:
 * - Wenn alle 7 Tage gleich → "Mo-So: 17:00-22:00"
 * - Wenn aufeinanderfolgende gleich → "Mi-Fr: 17:00-22:00 / Dienstag: Ruhetag"
 * - Sonst pro Tag.
 *
 * OHNE Einstellungen wird NICHTS gerendert — siehe Begründung im Rumpf.
 */
function OpeningHoursList({ hours }: { hours: unknown }) {
  const list = Array.isArray(hours) ? (hours as OpeningHour[]) : null;

  // KEIN Ersatztext mehr. Der frühere lautete „Di - So: 17:00 - 23:00 Uhr" /
  // „Montag: Ruhetag" — beides falsch: Ruhetag ist DIENSTAG, und um 22:00 ist
  // Schluss. Das prerenderte HTML liefert die echten Zeiten (13× im
  // Schnappschuss belegt, der Ersatztext 0×); React ersetzt den Baum beim Mount
  // aber durch diesen Zweig, bis die Einstellungen da sind — der Gast las in
  // diesem Fenster den falschen Ruhetag. Seit der supabase-Chunk aus dem
  // Vorlade-Pfad genommen wurde, trafen die Daten auf Prod erst nach 6375 ms
  // statt 5673 ms ein; das Fenster ist also länger geworden, nicht kürzer.
  // Nichts zeigen ist ehrlicher als Falsches zeigen.
  if (!list || list.length === 0) return null;

  // Group consecutive days with same hours
  const groups: Array<{ days: OpeningHour['day'][]; closed: boolean; open?: string; close?: string }> = [];
  for (const day of WEEKDAYS) {
    const h = list.find((x) => x.day === day);
    if (!h) continue;
    const prev = groups[groups.length - 1];
    if (
      prev &&
      prev.closed === h.closed &&
      prev.open === h.open &&
      prev.close === h.close
    ) {
      prev.days.push(day);
    } else {
      groups.push({
        days: [day],
        closed: h.closed,
        open: h.open,
        close: h.close,
      });
    }
  }

  return (
    <>
      {groups.map((g, i) => {
        const firstDay = g.days[0];
        const lastDay = g.days[g.days.length - 1];
        if (!firstDay || !lastDay) return null;
        const range =
          g.days.length === 1
            ? WEEKDAY_LABELS[firstDay]
            : `${WEEKDAY_LABELS[firstDay].slice(0, 2)} - ${WEEKDAY_LABELS[lastDay].slice(0, 2)}`;
        return (
          <p key={i}>
            {range}: {g.closed ? 'Ruhetag' : `${g.open ?? ''} - ${g.close ?? ''} Uhr`}
          </p>
        );
      })}
    </>
  );
}
